import React, { ReactElement, cloneElement, isValidElement, ReactNode, createElement, CSSProperties } from 'react';
import NextLink, { LinkProps } from 'next/link';
import { useStoreState } from 'base/store';
import { getNativePage } from 'base/utils/app';
import site from 'pro/config/site';

export type ILinkProps = Omit<LinkProps, 'href'> & Globals.TrackAttributes & {
    href: LinkProps['href'] | undefined;
    hrefEmptyRender?: (children: ReactNode) => ReactElement;
    hrefEmptyTag?: string;
    target?: HTMLAnchorElement['target'];
    rel?: HTMLAnchorElement['rel'];
    children?: ReactNode;
    className?: string;
    style?: CSSProperties;
    dangerouslySetInnerHTML?: {
        __html: string;
    };
};

function filterFields(data: Record<string, any>, keepKeys): Record<string, any> {
    const result: Record<string, any> = {};
    Object.entries(data).forEach(([key, value]) => {
        if(keepKeys.includes(key) || key.startsWith('on') || key.startsWith('data-')) {
            result[key] = value;
        }
    });
    return result;
}


export const Link = React.forwardRef<HTMLAnchorElement, ILinkProps>((props, ref) => {
    const { ua: { isApp } } = useStoreState('rootStore');

    const {
        href,
        as,
        replace,
        scroll,
        shallow,
        passHref,
        prefetch = false,
        locale,
        legacyBehavior,
        children,
        target,
        rel,
        hrefEmptyRender,
        hrefEmptyTag = 'span',
        ...rest
    } = props;

    const commonTagProps = {
        ref,
        ...filterFields(props, [
            'className',
            'style',
            'dangerouslySetInnerHTML',
        ])
    };

    if (!href || (typeof href === 'string' && href.trim() === '')) {
        return hrefEmptyRender
            ? hrefEmptyRender(children)
            : createElement(hrefEmptyTag, commonTagProps, children);
    }

    if (isApp && typeof href === 'string') {
        const pathname = href.replace(/[?#].*$/, '');
        const nativePage = getNativePage(pathname);

        if (nativePage) {
            if (typeof children === 'string' || typeof children === 'number') {
                return (
                    <a
                        {...commonTagProps}
                        onClick={(e) => {
                            nativePage.callback(pathname, href)
                            e.preventDefault();
                        }}
                    >
                        {children}
                    </a>
                )
            } else if (isValidElement(children)) {
                return cloneElement((children as ReactElement), {
                    commonTagProps,
                    onClick: (e: any) => {
                        nativePage.callback(pathname, href)
                        e.preventDefault();
                    },
                })
            } else {
                return (
                    <span
                        {...commonTagProps}
                        onClick={() => {
                            nativePage.callback(pathname, href)
                        }}
                    >
                        {children}
                    </span>
                )
            }
        }
    }

    return (
        <NextLink
            href={href}
            as={as}
            replace={replace}
            scroll={scroll}
            shallow={shallow}
            passHref={passHref}
            prefetch={prefetch}
            locale={locale}
            legacyBehavior={legacyBehavior}
        >
            <a
                target={target}
                rel={rel || (target === '_blank' ? 'nofollow noopener noreferrer' : void 0)}
                {...commonTagProps}
            >
                {children}
            </a>
        </NextLink>
    )
});

export default Link;